import { Pipe, PipeTransform, inject } from '@angular/core';
import { EnvironmentService } from '../../core/services/environment.service';

/**
 * Pipe to prefix asset paths with the current (or overridden) artwork environment path.
 *
 * Usage in templates:
 *   <img [src]="'logos/checkpoint-logo-horizontal.png' | envPrefix" />
 *
 * Resolves to:
 *   - Normal: /artwork/development/logos/checkpoint-logo-horizontal.png
 *   - Override active: /artwork/production/logos/checkpoint-logo-horizontal.png
 *
 * This pipe is impure to reactively update when the artwork override changes.
 * It reads from the artworkPath signal which changes when override is set/cleared.
 */
@Pipe({
  name: 'envPrefix',
  standalone: true,
  pure: false, // Impure to react to signal changes in EnvironmentService
})
export class EnvPrefixPipe implements PipeTransform {
  private environmentService = inject(EnvironmentService);

  /**
   * @param variant 'brand' resolves to production artwork (unless a demo override is active);
   *                omitted resolves to the current environment's artwork.
   */
  transform(assetPath: string, variant?: 'brand'): string {
    // Remove leading slash if present
    const cleanPath = assetPath.startsWith('/') ? assetPath.slice(1) : assetPath;

    // Get current artwork path (with override if active) - reactive via signal
    const artworkPath =
      variant === 'brand' ? this.environmentService.brandArtworkPath() : this.environmentService.artworkPath();

    return `/${artworkPath}/${cleanPath}`;
  }
}
